import { useCallback, useContext, useEffect, useState } from "react";
import "./ConfirmDeleteOverlayFn.scss";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import UiContext from "../../../contexts/UiContext";

type Props = {
    /**
     * The id of the element to attach the overlay to, such as 'row-1
     */
    targetElem: string | null;
    /**
     * The handler when the confirm is clicked on the overlay
     *
     * @param data
     */
    handleConfirm: (data?: any) => void;
    /**
     * The handler when cancel is clicked on the overlay
     */
    handleCancel: () => void;
    /**
     * Override the default message (confirm_delete)
     */
    message?: string;
    /**
     * Id for the element that this component to overlay will display inside of.
     */
    nestedParentElem?: string;
    /**
     * If there needs to be any adjustment left or right (negative) for the overlay
     */
    leftAdjustment?: number;
    /**
     * If there needs to be any adjustment top or bottom (negative) for the overlay
     */
    topAdjustment?: number;
    /**
     * Don't include the main view scrolling when determining the positioning
     */
    skipMainView?: boolean;
    /**
     * If the left adjustment should be skipped all together, and be set to 0
     */
    skipLeftAdjustment?: boolean;
    /**
     * Class name to add to the overlay
     */
    className?: string;
};

/**
 * ##### ConfirmDeleteOverlayFn Component
 *
 * **Usage:**
 * ```jsx
 * <ConfirmDeleteOverlayFn
 *    targetElem={deleteElem}
 *    handleConfirm={confirmDelete}
 *    handleCancel={cancelDelete}
 *    nestedParentElem={'intake-forms'}
 *    skipMainView={true}
 * />
 * ```
 *
 * The best way to use this component is to set the parent surrounding div/element with position-relative
 * And then set an id value on the component that you want to overlay the delete confirmation on, and have
 * the component skip the main view for calculating scrollTo
 *
 */
export const ConfirmDeleteOverlayFn = (props: Props) => {
    const uiContext = useContext(UiContext);

    const mainView = document.getElementById("mainView");
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayPosition, setOverlayPosition] = useState<{ top: number; left: number; width: number; height: number }>({
        top: 0,
        left: 0,
        width: 0,
        height: 0,
    });
    const [isTall, setIsTall] = useState(false);

    const handleOverlay = () => {
        if (props.targetElem?.length) {
            const targetElement = document.getElementById(props.targetElem);
            const rowRect = targetElement?.getBoundingClientRect();
            const mainViewRect = mainView?.getBoundingClientRect();

            let parentElemRect: DOMRect | undefined;

            if (props.nestedParentElem?.length) {
                parentElemRect = document.getElementById(props.nestedParentElem)?.getBoundingClientRect();
            }

            if (rowRect && mainView && mainViewRect) {
                // Account for mainView's position and scroll
                let scrollTop = mainView.scrollTop;
                const scrollLeft = mainView.scrollLeft;

                // Do we need to do the same for the top?
                let mainViewRectLeft = mainViewRect.left;

                if (parentElemRect) {
                    mainViewRectLeft = parentElemRect.left;
                }

                if (props.leftAdjustment) {
                    mainViewRectLeft += props.leftAdjustment;
                }

                let mainViewRectTop = mainViewRect.top;

                if (props.skipMainView) {
                    mainViewRectTop = 0;
                    scrollTop = 0;
                }

                let top = rowRect.top + mainViewRectTop + scrollTop;

                if (props.skipMainView && props.nestedParentElem) {
                    top = rowRect.top - (parentElemRect?.top || 0) + scrollTop;
                }

                let left = rowRect.left - mainViewRectLeft + scrollLeft;

                if (props.skipLeftAdjustment) {
                    left = 0;
                }

                if (props.topAdjustment) {
                    top = top - props.topAdjustment;
                }

                setOverlayPosition({
                    top,
                    left,
                    width: rowRect.width,
                    height: rowRect.height,
                });

                setIsTall(rowRect.height > 100);
                setOverlayVisible(true);
            }
        } else {
            setOverlayVisible(false);
            setOverlayPosition({ top: 0, left: 0, width: 0, height: 0 });
        }
    };

    const scrollHandler = useCallback((event: any) => {
        // console.log("scrollHandler");
        // handleOverlay();
    }, []);

    const addScrollListener = () => {
        mainView?.addEventListener("scroll", scrollHandler, { capture: true });
    };

    const removeScrollListener = () => {
        mainView?.removeEventListener("scroll", scrollHandler, { capture: true });
    };

    useEffect(() => {
        if (overlayVisible) {
            addScrollListener();
        } else {
            removeScrollListener();
        }
    }, [overlayVisible]);

    useEffect(() => {
        return () => {
            removeScrollListener();
        };
    }, []);

    useEffect(() => {
        handleOverlay();
    }, [uiContext.menuOpen, uiContext.menuIconMode]);

    useEffect(() => {
        handleOverlay();
    }, [props.targetElem]);

    const cancelFn = () => {
        removeScrollListener();
        props.handleCancel();
    };

    const confirmFn = () => {
        removeScrollListener();
        props.handleConfirm();
    };

    return overlayVisible ? (
        <div className={"ConfirmDeleteOverlayFn"}>
            <div
                className={(props.className || "") + " overlay " + (isTall ? "tall" : "")}
                style={{
                    top: overlayPosition.top,
                    left: overlayPosition.left,
                    position: "absolute",
                    width: overlayPosition.width,
                    height: overlayPosition.height,
                }}>
                <div className={"overlay-content"}>
                    <div className={"overlay-content-actions"}>
                        <OwButton
                            centerIconClass={"fad fa-trash-alt"}
                            className={"btn-danger btn-sm ms-2"}
                            fn={confirmFn}
                            ariaLabel={"Confirm Delete"}
                        />
                        <OwButton
                            centerIconClass={"fad fa-times"}
                            className={"btn-secondary btn-sm ms-2"}
                            fn={cancelFn}
                            ariaLabel={"Cancel Delete"}
                        />
                    </div>
                    <div className={"overlay-content-title ms-4"}>
                        <OwLabel title={props.message || "confirm_delete"} noBottomMargin={true} />
                    </div>
                </div>
            </div>
        </div>
    ) : null;
};
